/**
 * AppsflyerNetworkPostbackInstallsInAppEventsPage — view + filter incoming
 * Mobile MMP postback events (install + in-app events) scoped to tenant.
 * GREEN-only accent palette.
 */
import { useMemo, useState, type FormEvent } from "react";
import { usePostbackEventsList, usePostbackEventCreate } from "./hooks";
import type { PostbackEventRow } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const PILL_GREEN =
	"inline-flex items-center rounded-[6px] bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20";

function formatEpoch(seconds: number | null | undefined): string {
	if (!seconds || !Number.isFinite(seconds)) return "";
	const d = new Date(seconds * 1000);
	return d.toISOString().replace("T", " ").replace("Z", " UTC").slice(0, 23);
}

function formatRevenue(
	amount: number | null,
	currency: string | null,
): string {
	if (amount === null || amount === undefined || !Number.isFinite(amount)) {
		return "-";
	}
	const cur = currency && currency.length === 3 ? currency : "USD";
	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: cur,
			maximumFractionDigits: 4,
		}).format(amount);
	} catch {
		return `${amount.toFixed(2)} ${cur}`;
	}
}

export function AppsflyerNetworkPostbackInstallsInAppEventsPage() {
	const [eventNameFilter, setEventNameFilter] = useState("");
	const listQuery = usePostbackEventsList(
		eventNameFilter ? { event_name: eventNameFilter, limit: 200 } : { limit: 200 },
	);
	const createMut = usePostbackEventCreate();

	const [afEventId, setAfEventId] = useState("");
	const [eventName, setEventName] = useState("install");

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		const id = afEventId.trim();
		const name = eventName.trim() || "install";
		if (!id) return;
		createMut.mutate(
			{
				af_event_id: id,
				event_name: name,
				event_time: Math.floor(Date.now() / 1000),
			},
			{
				onSuccess: () => {
					setAfEventId("");
				},
			},
		);
	};

	const items: PostbackEventRow[] = listQuery.data?.items ?? [];

	const stats = useMemo(() => {
		let installs = 0;
		let inApp = 0;
		let revenue = 0;
		let revenueCurrency: string | null = null;
		for (const row of items) {
			if (row.event_name === "install") installs += 1;
			else inApp += 1;
			if (row.event_revenue !== null && Number.isFinite(row.event_revenue)) {
				revenue += row.event_revenue;
				if (!revenueCurrency && row.event_revenue_currency) {
					revenueCurrency = row.event_revenue_currency;
				}
			}
		}
		return { installs, inApp, revenue, revenueCurrency };
	}, [items]);

	return (
		<main
			data-testid="appsflyer-network-postback-installs-in-app-events-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Mobile MMP postback
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					AppsFlyer Network Postback
				</h1>
				<p className="text-sm text-zinc-600">
					Inbound S2S postbacks for install and in-app events, scoped to
					your tenant. Idempotent on the network event id.
				</p>
			</header>

			<section
				className="grid grid-cols-1 gap-4 sm:grid-cols-3"
				aria-label="Postback summary"
				data-testid="appsflyer-network-postback-installs-in-app-events-stats"
			>
				<div className={CARD}>
					<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						Install events
					</p>
					<p className="mt-2 text-2xl font-semibold text-green-700">
						{stats.installs}
					</p>
				</div>
				<div className={CARD}>
					<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						In-app events
					</p>
					<p className="mt-2 text-2xl font-semibold text-green-700">
						{stats.inApp}
					</p>
				</div>
				<div className={CARD}>
					<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						Attributed revenue
					</p>
					<p className="mt-2 text-2xl font-semibold text-green-700">
						{formatRevenue(stats.revenue, stats.revenueCurrency)}
					</p>
				</div>
			</section>

			<section className={CARD} aria-label="Record postback event">
				<h2 className="text-base font-semibold text-zinc-900">
					Record postback event
				</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Simulate an inbound S2S postback for the current tenant. Dedup
					key: network event id.
				</p>
				<form
					onSubmit={handleCreate}
					className="mt-4 flex flex-col gap-3 sm:flex-row"
					data-testid="appsflyer-network-postback-installs-in-app-events-create-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="Network event id"
						value={afEventId}
						onChange={(e) => setAfEventId(e.currentTarget.value)}
						disabled={createMut.isPending}
						data-testid="appsflyer-network-postback-installs-in-app-events-af-event-id-input"
					/>
					<input
						type="text"
						className={INPUT}
						placeholder="Event name (install, purchase, .)"
						value={eventName}
						onChange={(e) => setEventName(e.currentTarget.value)}
						disabled={createMut.isPending}
						data-testid="appsflyer-network-postback-installs-in-app-events-event-name-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={createMut.isPending || !afEventId.trim()}
						data-testid="appsflyer-network-postback-installs-in-app-events-submit"
					>
						{createMut.isPending ? "Recording." : "Record"}
					</button>
				</form>
				{createMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{createMut.error.message}
					</p>
				) : null}
				{createMut.data ? (
					<p
						className="mt-2 text-xs text-green-700"
						data-testid="appsflyer-network-postback-installs-in-app-events-create-result"
					>
						{createMut.data.idempotent
							? "Replay accepted (already recorded)."
							: "Recorded."}
					</p>
				) : null}
			</section>

			<section
				className={CARD}
				aria-label="AppsFlyer Network Postback events"
			>
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Recent events
					</h2>
					<div className="flex items-center gap-2">
						<label
							className="text-xs font-medium text-zinc-500"
							htmlFor="postback-event-filter"
						>
							Filter by event name
						</label>
						<input
							id="postback-event-filter"
							type="text"
							className={INPUT + " max-w-[220px]"}
							placeholder="All events"
							value={eventNameFilter}
							onChange={(e) =>
								setEventNameFilter(e.currentTarget.value)
							}
							data-testid="appsflyer-network-postback-installs-in-app-events-filter-input"
						/>
						{eventNameFilter ? (
							<button
								type="button"
								className={BTN_GHOST}
								onClick={() => setEventNameFilter("")}
								data-testid="appsflyer-network-postback-installs-in-app-events-filter-clear"
							>
								Clear
							</button>
						) : null}
					</div>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="appsflyer-network-postback-installs-in-app-events-loading"
					>
						Loading events.
					</p>
				) : listQuery.error ? (
					<p className="mt-4 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="appsflyer-network-postback-installs-in-app-events-empty"
					>
						No postback events yet. Record one above or wait for
						inbound traffic.
					</p>
				) : (
					<div
						className="mt-4 overflow-x-auto"
						data-testid="appsflyer-network-postback-installs-in-app-events-list"
					>
						<table className="min-w-full text-sm">
							<thead>
								<tr className="border-b border-zinc-200 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
									<th className="px-2 py-2">Received</th>
									<th className="px-2 py-2">Event time</th>
									<th className="px-2 py-2">Event name</th>
									<th className="px-2 py-2">Network event id</th>
									<th className="px-2 py-2">App id</th>
									<th className="px-2 py-2">Revenue</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200">
								{items.map((row) => (
									<tr
										key={row.id}
										data-testid={`appsflyer-network-postback-installs-in-app-events-row-${row.id}`}
									>
										<td className="px-2 py-2 font-mono text-xs text-zinc-600">
											{formatEpoch(row.received_at)}
										</td>
										<td className="px-2 py-2 font-mono text-xs text-zinc-600">
											{formatEpoch(row.event_time)}
										</td>
										<td className="px-2 py-2">
											<span className={PILL_GREEN}>
												{row.event_name}
											</span>
										</td>
										<td className="px-2 py-2 font-mono text-xs text-zinc-700">
											{row.af_event_id}
										</td>
										<td className="px-2 py-2 text-xs text-zinc-600">
											{row.app_id ?? "-"}
										</td>
										<td className="px-2 py-2 text-right text-xs text-zinc-700">
											{formatRevenue(
												row.event_revenue,
												row.event_revenue_currency,
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default AppsflyerNetworkPostbackInstallsInAppEventsPage;
